/**
 * Resolve scaled protein / carbs, calorie range, and explicit portion for
 * subscription catalog cards and purchased dashboard subscription cards.
 */
import type { SubscriptionData } from '../types';
import { getCatalogPlanPortion, parsePortion } from './subscriptionPortion';

export type ResolvedScaledMacros = {
  scaled_protein: number | null;
  scaled_carbs: number | null;
};

export type ResolvedCalorieRange = {
  min_calories: number | null;
  max_calories: number | null;
};

export type SubscriptionCardNutritionFields = ResolvedScaledMacros &
  ResolvedCalorieRange & {
    /** Explicit portion/persons from the payload only (never inferred from protein). */
    portion: number | null;
  };

export type SubscriptionCardNutritionDisplay = {
  /** When true, show portion and suppress scaled macros + calorie range. */
  show_portion: boolean;
  show_scaled_macros: boolean;
  show_calories_limit: boolean;
  fields: SubscriptionCardNutritionFields;
};

function parsePositiveNumber(raw: unknown): number | null {
  if (raw == null || raw === '') {
    return null;
  }
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n <= 0) {
    return null;
  }
  return n;
}

function firstPositive(...candidates: unknown[]): number | null {
  for (const candidate of candidates) {
    const value = parsePositiveNumber(candidate);
    if (value != null) {
      return value;
    }
  }
  return null;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value != null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;
}

function hasResolvedCalorieRange(range: ResolvedCalorieRange): boolean {
  return range.min_calories != null || range.max_calories != null;
}

/** Explicit portion on catalog plan (no default to 1). */
export function resolveCatalogPlanExplicitPortion(plan: unknown): number | null {
  return getCatalogPlanPortion(asRecord(plan) ?? null);
}

/**
 * Explicit portion on purchased subscription only.
 * Does not fall back to `maximum_protein` or default `1` (unlike `getSubscriptionPortionAsPersons`).
 */
export function resolvePurchasedSubscriptionExplicitPortion(
  subscription: SubscriptionData | null | undefined,
): number | null {
  if (!subscription) {
    return null;
  }
  const detail = asRecord(subscription.detail);
  const subscription_detail = asRecord(subscription.subscription_detail);
  const particulars = asRecord(subscription.particularsMacros ?? (subscription as { particulars?: unknown }).particulars);
  const root = subscription as unknown as Record<string, unknown>;

  return (
    parsePortion(detail?.portion) ??
    parsePortion(detail?.portions) ??
    parsePortion(detail?.portion_size) ??
    parsePortion(subscription_detail?.portion) ??
    parsePortion(particulars?.portion) ??
    parsePortion(root.portion) ??
    parsePortion(root.portions) ??
    parsePortion(root.portion_size) ??
    parsePortion(root.no_of_people) ??
    parsePortion(root.persons) ??
    parsePortion(root.person_count) ??
    parsePortion(root.number_of_people) ??
    parsePortion(root.people) ??
    parsePortion(detail?.persons) ??
    parsePortion(detail?.person_count) ??
    parsePortion(particulars?.no_of_people) ??
    parsePortion(particulars?.people) ??
    parsePortion(particulars?.number_of_people)
  );
}

/** Catalog: `scaled_*` preferred, then `protein` / `carbs`. */
export function resolveCatalogPlanScaledMacros(plan: unknown): ResolvedScaledMacros {
  const root = asRecord(plan);
  if (!root) {
    return { scaled_protein: null, scaled_carbs: null };
  }

  const detail = asRecord(root.detail);

  const scaled_protein = firstPositive(root.scaled_protein, detail?.scaled_protein, root.protein, detail?.protein);

  const scaled_carbs = firstPositive(root.scaled_carbs, detail?.scaled_carbs, root.carbs, detail?.carbs);

  return { scaled_protein, scaled_carbs };
}

/** Purchased: `detail.maximum_protein` + `detail.maximum_carbs`. */
export function resolveSubscriptionScaledMacros(subscription: SubscriptionData | null | undefined): ResolvedScaledMacros {
  if (!subscription) {
    return { scaled_protein: null, scaled_carbs: null };
  }

  const detail = asRecord(subscription.detail);
  const subscription_detail = asRecord(subscription.subscription_detail);
  const root = subscription as unknown as Record<string, unknown>;

  const scaled_protein = firstPositive(
    detail?.maximum_protein,
    subscription_detail?.maximum_protein,
    root.maximum_protein,
  );

  const scaled_carbs = firstPositive(detail?.maximum_carbs, subscription_detail?.maximum_carbs, root.maximum_carbs);

  return { scaled_protein, scaled_carbs };
}

export function resolveCatalogPlanCalorieRange(plan: unknown): ResolvedCalorieRange {
  const root = asRecord(plan);
  if (!root) {
    return { min_calories: null, max_calories: null };
  }
  const detail = asRecord(root.detail);
  return {
    min_calories: firstPositive(root.minimum_calories, detail?.minimum_calories),
    max_calories: firstPositive(root.maximum_calories, detail?.maximum_calories),
  };
}

export function resolvePurchasedSubscriptionCalorieRange(
  subscription: SubscriptionData | null | undefined,
): ResolvedCalorieRange {
  if (!subscription) {
    return { min_calories: null, max_calories: null };
  }
  const detail = asRecord(subscription.detail);
  const subscription_detail = asRecord(subscription.subscription_detail);
  const root = subscription as unknown as Record<string, unknown>;
  return {
    min_calories: firstPositive(detail?.minimum_calories, subscription_detail?.minimum_calories, root.minimum_calories),
    max_calories: firstPositive(detail?.maximum_calories, subscription_detail?.maximum_calories, root.maximum_calories),
  };
}

export function hasResolvedScaledMacros(macros: ResolvedScaledMacros): boolean {
  return macros.scaled_protein != null || macros.scaled_carbs != null;
}

/** Drop missing / sentinel caps (merge pipeline uses 9999999 when API omits max). */
export function normalizePlanCalorieLimit(raw: number | null | undefined): number {
  if (raw == null || !Number.isFinite(raw) || raw <= 0 || raw >= 9_999_999) {
    return 0;
  }
  return Math.round(raw);
}

/**
 * Dashboard daily-nutrition / plan-rhythm calorie goal.
 * Uses plan `maximum_calories` (day config or purchased sub) when `calories_limit` is on.
 */
export function resolveDashboardCalorieGoal(options: {
  calories_limit_enabled: boolean;
  day_config?: { min_calories?: number; max_calories?: number } | null;
  subscription?: SubscriptionData | null;
}): number {
  if (!options.calories_limit_enabled) {
    return 0;
  }
  const from_day_max = normalizePlanCalorieLimit(options.day_config?.max_calories);
  if (from_day_max > 0) {
    return from_day_max;
  }
  const from_day_min = normalizePlanCalorieLimit(options.day_config?.min_calories);
  if (from_day_min > 0) {
    return from_day_min;
  }
  const range = resolvePurchasedSubscriptionCalorieRange(options.subscription);
  return normalizePlanCalorieLimit(range.max_calories) || normalizePlanCalorieLimit(range.min_calories);
}

export function resolveCatalogPlanCardNutrition(plan: unknown): SubscriptionCardNutritionFields {
  const macros = resolveCatalogPlanScaledMacros(plan);
  const calories = resolveCatalogPlanCalorieRange(plan);
  return {
    ...macros,
    ...calories,
    portion: resolveCatalogPlanExplicitPortion(plan),
  };
}

export function resolvePurchasedSubscriptionCardNutrition(
  subscription: SubscriptionData | null | undefined,
): SubscriptionCardNutritionFields {
  const macros = resolveSubscriptionScaledMacros(subscription);
  const calories = resolvePurchasedSubscriptionCalorieRange(subscription);
  return {
    ...macros,
    ...calories,
    portion: resolvePurchasedSubscriptionExplicitPortion(subscription),
  };
}

/**
 * Portion shown when present **and** `show_portion` gate is on.
 * When portion is shown, scaled macros + calorie range are forced off.
 * Labeling uses `portion_text` at the UI layer.
 */
export function resolveSubscriptionCardNutritionDisplay(
  fields: SubscriptionCardNutritionFields,
  gates: { show_scaled_macros: boolean; calories_limit: boolean; show_portion: boolean },
): Omit<SubscriptionCardNutritionDisplay, 'fields'> & { fields: SubscriptionCardNutritionFields } {
  const has_portion = fields.portion != null;
  const show_portion = has_portion && gates.show_portion;
  const show_scaled_macros =
    !show_portion && gates.show_scaled_macros && hasResolvedScaledMacros(fields);
  const show_calories_limit = !show_portion && gates.calories_limit && hasResolvedCalorieRange(fields);

  return {
    show_portion,
    show_scaled_macros,
    show_calories_limit,
    fields,
  };
}
